import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, cellsOf, chooser, cycle, find, fitForm, pcs, phrase, shuffle, titleOf, totalBars, trackId, transpose, tune } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

type Chord = { pad: readonly string[]; root: string; major: boolean };

const CHORDS: Readonly<Record<string, Chord>> = {
  Cm: { pad: ['C3', 'G3', 'Eb4', 'G4'], root: 'C2', major: false },
  Ab: { pad: ['Ab2', 'Eb3', 'C4', 'G4'], root: 'Ab1', major: true },
  Fm: { pad: ['F2', 'C3', 'Ab3', 'F4'], root: 'F1', major: false },
  G: { pad: ['G2', 'D3', 'B3', 'F4'], root: 'G1', major: true },
  Bb: { pad: ['Bb2', 'F3', 'D4', 'F4'], root: 'Bb1', major: true },
  Eb: { pad: ['Eb3', 'Bb3', 'D4', 'G4'], root: 'Eb2', major: true },
};

const PROGS = [
  ['Cm', 'Ab', 'Fm', 'G'],
  ['Cm', 'Bb', 'Ab', 'Bb'],
  ['Cm', 'Fm', 'Ab', 'G'],
  ['Cm', 'Ab', 'Eb', 'Bb'],
] as const;

const BELL: Note[][] = [
  [[0, 'G5', 2], [2, 'Eb5', 2], [4, 'C5', 2], [6, 'D5', 2], [8, 'Eb5', 3], [11, 'D5', 1, 0.7], [12, 'C5', 4]],
  [[0, 'Eb5', 2], [2, 'C5', 2], [4, 'Ab4', 2], [6, 'C5', 2], [8, 'Eb5', 3], [11, 'F5', 1, 0.7], [12, 'G5', 4]],
  [[0, 'Ab5', 2], [2, 'G5', 2], [4, 'F5', 2], [6, 'Eb5', 2], [8, 'C5', 3], [11, 'Eb5', 1, 0.7], [12, 'F5', 4]],
  [[0, 'G5', 2], [2, 'F5', 2], [4, 'Eb5', 2], [6, 'D5', 2], [8, 'B4', 4], [12, 'D5', 2], [14, 'F5', 2]],
];

const COUNTER: Note[][] = [
  [[0, 'G4', 7], [8, 'Eb4', 7]],
  [[0, 'Ab4', 7], [8, 'C5', 7]],
  [[0, 'F4', 7], [8, 'Ab4', 7]],
  [[0, 'G4', 7], [8, 'B4', 7]],
];

type Row = readonly (readonly [step: number, semis: number, len: number, glide?: readonly [to: number, at: number]])[];

const DROP_808: readonly Row[] = [
  [[0, 0, 6.5], [7, 0, 2.8], [10, 12, 5.5, [0, 2]]],
  [[0, 0, 6.5], [7, 0, 3.5], [11, 7, 4.5]],
  [[0, 0, 6.5], [7, 0, 2.8], [10, 12, 5.5, [0, 2]]],
  [[0, 0, 5.5], [6, 0, 1.8], [8, 7, 2.8], [11, 0, 2.8], [14, 4, 1.9, [5, 1]]],
];

const VERSE_808: readonly Row[] = [
  [[0, 0, 9.5], [10, 0, 5.5]],
  [[0, 0, 9.5], [10, 0, 3], [13, 2, 2.8]],
  [[0, 0, 9.5], [10, 12, 5.5, [0, 1.5]]],
  [[0, 0, 7.5], [8, 0, 3.5], [12, 7, 3.5, [0, 2]]],
];

type Hats = { base: string; roll: { from: number; to: number; n: number } | null };

const HATS: readonly Hats[] = [
  { base: 'x.x.x.x.x.x.x.xx', roll: null },
  { base: 'x.x.x.x.xxxx....', roll: { from: 12, to: 16, n: 6 } },
  { base: 'x.x.x.xxx.x.x.x.', roll: null },
  { base: 'x.x.x.x.x.x.....', roll: { from: 12, to: 16, n: 8 } },
];

const KEYS = [0, -2, 2, -3, 1, 3, -1] as const;
const PERCS = ['...x.......x..x.', '..x....x...x..x.', '...x..x....x.x..'] as const;
const MINOR = [0, 2, 3, 5, 7, 8, 10] as const;
const ROLL_COUNTS = [4, 8, 16, 24] as const;

const PLAN: FormPlan = {
  intro: 8,
  outro: 4,
  body: [
    { name: 'dropA', bars: 16, min: 8, rank: 1 },
    { name: 'verse', bars: 16, min: 8, rank: 3 },
    { name: 'break', bars: 4, min: 4, rank: 4, with: 'dropB' },
    { name: 'dropB', bars: 16, min: 8, rank: 2 },
  ],
};

function rowsOf(pick: (i: number, c: Chord) => number, pool: readonly Row[], prog: readonly Chord[]): Row[] {
  return prog.map((c, i) => pool[pick(i, c)]!);
}

function score(seed: string, p: Params): Score {
  const ch = chooser(TRAP, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick(PROGS).map((n) => CHORDS[n]!);
  const drop808 = ch.orig ? [...DROP_808] : rowsOf((_, c) => (c.major && ch.rng.chance(0.5) ? 3 : ch.rng.int(0, 2)), DROP_808, prog);
  const verse808 = ch.orig ? [...VERSE_808] : rowsOf((_, c) => (c.major && ch.rng.chance(0.3) ? 3 : ch.rng.int(0, 3)), VERSE_808, prog);
  const hats = ch.orig ? [...HATS] : shuffle(ch.rng, HATS);
  const percP = ch.pick(PERCS);
  const harmonies = prog.map((c) => [{ at: 0, tones: pcs(c.pad) }]);
  const bellRows = ch.orig ? BELL : phrase(ch.rng, BELL.map(cellsOf), harmonies, MINOR, [67, 80], 79);
  let prev = 67;
  const counterRows = ch.orig
    ? COUNTER
    : harmonies.map((h) => {
        const r = tune(ch.rng, [[0, 7], [8, 7]], h, MINOR, [65, 72], prev, false);
        prev = r.last;
        return r.notes;
      });
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);
  const tonic = tr(prog[0]!.root);

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, 50, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const outro = total - form[form.length - 1]!.bars;
  const dropA = find(form, 'dropA')!;
  const dropB = find(form, 'dropB');
  const brk = find(form, 'break');
  const verseAt = find(form, 'verse');
  const rng = createRng(`trap-${seed}`);
  const human = (v: number) => v * (0.9 + rng.float() * 0.2);

  const events: Ev[] = [];
  const hit = (v: string, bar: number, step: number, vel: number) => events.push({ v, t: g.at(bar, step), bar, step, vel });
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });

  const autos: Score['autos'] = [
    { v: 'bellLp', points: [[g.at(0), 700], [g.at(dropA) - s * 4, 2400]] },
    {
      v: 'bellLp',
      points:
        brk === undefined || dropB === undefined
          ? [[g.at(dropA), 9000]]
          : [[g.at(dropA), 9000], [g.at(brk), 9000], [g.at(brk) + s, 900], [g.at(dropB) - s, 3000]],
    },
    {
      v: 'bellLp',
      points: dropB === undefined ? [[g.at(outro), 9000], [g.at(total), 700]] : [[g.at(dropB), 9000], [g.at(outro), 9000], [g.at(total), 700]],
    },
    { v: 'padLp', points: [[g.at(0), 500], [g.at(dropA), 1400]] },
  ];

  const play808 = (row: Row, root: string, bar: number) => {
    for (const [step, semis, len, glide] of row) {
      const ev: Ev = { v: 'b808', t: g.at(bar, step), bar, step, vel: 1, dur: len * s, note: tr(transpose(root, semis)) };
      if (glide !== undefined) ev.glide = { to: tr(transpose(root, glide[0])), at: glide[1] * s, time: s * 0.9 };
      events.push(ev);
      hit('kick', bar, step, step === 0 ? 1 : 0.85);
    }
  };

  for (const b of g.bars()) {
    const loop = cycle(b.bar - intro, 4);
    const c = prog[loop]!;
    const drop = b.name === 'dropA' || b.name === 'dropB';
    const verse = b.name === 'verse';

    if (b.name !== 'outro' || b.index < b.bars - 1) {
      events.push({ v: 'pad', t: g.at(b.bar, 0), bar: b.bar, step: 0, vel: 0.7, dur: g.barLength * 0.97, notes: c.pad.map(tr) });
    }

    const bellOn = b.name === 'intro' || drop || b.name === 'break' || b.name === 'outro' || (verse && b.index % 2 === 0);
    if (bellOn) {
      for (const [step, note, len, vel] of bellRows[loop]!) {
        if (verse && step >= 8) continue;
        play('bell', b.bar, step, tr(note), len * s * 0.9, human(vel ?? 0.9));
      }
    }

    if (drop || verse) {
      play808((drop ? drop808 : verse808)[loop]!, c.root, b.bar);
      hit('clap', b.bar, 8, human(1));
      hit('snare', b.bar, 8, human(0.85));
      if (loop === 3) hit('snare', b.bar, 15, 0.35);
      const pattern = hats[verse ? (loop + 2) % 4 : loop]!;
      for (const h of hits(pattern.base)) hit('hat', b.bar, h.step, human(h.vel * (h.step % 4 === 0 ? 1 : 0.8)));
      if (pattern.roll !== null) {
        const { from, to, n } = pattern.roll;
        for (let i = 0; i < n; i++) hit('hat', b.bar, from + ((to - from) * i) / n, 0.45 + 0.4 * (i / n));
      }
      if ((loop === 1 || loop === 3) && e >= 0.3) hit('hatO', b.bar, 6, 0.7);
      if ((verse || b.name === 'dropB') && e >= 0.4) for (const h of hits(percP)) hit('perc', b.bar, h.step, human(h.vel));
    }

    if (b.name === 'dropB' && e >= 0.5) {
      for (const [step, note, len, vel] of counterRows[loop]!) play('counter', b.bar, step, tr(note), len * s, vel ?? 0.8);
      events.push({
        v: 'choir',
        t: g.at(b.bar, 0),
        bar: b.bar,
        step: 0,
        vel: 0.6,
        dur: g.barLength * 0.95,
        notes: c.pad.map((n) => tr(transpose(n, 12))),
      });
    }
    if (b.name === 'dropA' && ((b.index >= b.bars / 2 && e >= 0.55) || e >= 0.85)) {
      for (const [step, note, len, vel] of counterRows[loop]!) play('counter', b.bar, step, tr(note), len * s, (vel ?? 0.8) * 0.8);
    }

    if (b.name === 'break') {
      const r = Math.max(0, b.index - (b.bars - 4));
      const n = ROLL_COUNTS[r]!;
      for (let i = 0; i < n; i++) hit('hat', b.bar, (16 * i) / n, 0.35 + 0.5 * ((r * 16 + (16 * i) / n) / 64));
      if (b.index === 0) {
        play('b808', b.bar, 0, tonic, g.barLength * 0.9, 1);
        hit('kick', b.bar, 0, 1);
      }
    }
    if (b.name === 'outro' && b.index === 0) {
      play('b808', b.bar, 0, tonic, g.barLength * 1.5, 1);
      hit('kick', b.bar, 0, 1);
    }
  }

  const riseFrom = Math.max(0, dropA - 2);
  events.push({ v: 'rise', t: g.at(riseFrom), end: g.at(dropA) - s * 4, bar: riseFrom, step: 0, vel: 1 });
  if (brk !== undefined && dropB !== undefined) events.push({ v: 'rise', t: g.at(brk), end: g.at(dropB) - s * 4, bar: brk, step: 0, vel: 1 });
  for (const bar of [dropA, dropB]) {
    if (bar === undefined) continue;
    hit('crash', bar, 0, 1);
    hit('impact', bar, 0, 1);
  }
  if (verseAt !== undefined) hit('crash', verseAt, 0, 0.7);

  return {
    id: trackId(TRAP, seed, p),
    template: TRAP.id,
    seed,
    params: p,
    title: ch.orig ? TRAP.ref.title : titleOf(seed),
    genre: TRAP.genre,
    bpm: p.bpm,
    swing: 50,
    lufs: -10.5,
    key,
    form,
    mix: { reverbDecay: 3.4, delayTime: (60 / p.bpm) * 0.75, delayFeedback: 0.3, drive: 1.5, softness: 1.4, pumpDepth: 0.22, pumpRelease: 0.22 },
    events,
    autos,
    accents: { kick: 'kick', b808: '808', clap: 'snare', bell: 'melody', impact: 'drop' },
  };
}

export const TRAP: Template = {
  id: 'trap',
  genre: 'dark melodic trap',
  bpm: [130, 150],
  ref: { seed: 'neon-noturno', title: 'Neon Noturno', params: { bpm: 140, bars: 64, energy: 0.7 } },
  score,
};
